import pino from "pino";
import { noticeError } from "./newrelic/requests";

interface LogRecord {
  trace_id?: string;
  span_id?: string;
  trace_flags?: string;

  [key: string]: unknown;
}

export type PinoLogLevels =
  | "trace"
  | "debug"
  | "info"
  | "warn"
  | "error"
  | "fatal";

const logLevel = process.env.LOG_LEVEL || "info";

// https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#logseverity
const PinoToGcloudSeverityMap: Record<string, string> = {
  trace: "DEBUG",
  debug: "DEBUG",
  info: "INFO",
  warn: "WARNING",
  error: "ERROR",
  fatal: "CRITICAL",
};

const logTraceIdPrefix =
  process.env.LOG_TRACE_ID_PREFIX ?? "(LOG_TRACE_ID_PREFIX not set)";

const pinoLogger = pino({
  messageKey: "message",
  timestamp(): string {
    return `,"timestamp":"${new Date(Date.now()).toISOString()}"`;
  },
  level: logLevel,
  formatters: {
    log(object: LogRecord): Record<string, unknown> {
      const { trace_id, span_id, trace_flags, ...rest } = object;
      return {
        "logging.googleapis.com/trace": trace_id
          ? logTraceIdPrefix + trace_id
          : undefined,
        "logging.googleapis.com/spanId": span_id,
        "logging.googleapis.com/trace_sampled": trace_flags
          ? trace_flags === "01"
          : undefined,
        ...rest,
      };
    },
    level(label) {
      return {
        severity:
          PinoToGcloudSeverityMap[label] ?? PinoToGcloudSeverityMap["info"],
        level: label,
      };
    },
  },
});

const isServer = typeof window === "undefined";

export const logger = (
  level: PinoLogLevels,
  message: string,
  error?: unknown,
  options?: { [key: string]: string },
) => {
  if (isServer) {
    const messagePlusOptionalOptions = `${message} ${options ? `| ${JSON.stringify(options)}` : ""}`;
    if (error) {
      pinoLogger[level](error, messagePlusOptionalOptions);
    } else {
      pinoLogger[level](messagePlusOptionalOptions);
    }
  } else {
    noticeError(message, options);
  }
};
